import { useEffect, useRef, useState } from "react";
import "layouts/Quotation/quotation.css";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { LuCircleAlert, LuSave, LuTrash2, LuX } from "react-icons/lu";
import CatalogueItemModal from "components/CatalogueItemModal";
import { appConstants } from "utils/appConstants";
import { paymentTermsOptions, showResMsg } from "utils/generic";
import {
  normalizeQuotationCustomerField,
  sanitizeAustralianMobileInput,
  validateQuotationCustomerField,
  validateQuotationCustomerForm,
} from "utils/quotationCustomerValidation";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import useUnsavedModalChanges from "hooks/useUnsavedModalChanges";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";

const fields = [
  {
    name: "companyName",
    label: "Company name",
    maxLength: 200,
    autoComplete: "organization",
    required: true,
    placeholder: "Company or business name",
  },
  {
    name: "contactPerson",
    label: "Contact person",
    maxLength: 100,
    autoComplete: "name",
    placeholder: "e.g. Alex Morgan",
  },
  {
    name: "mobile",
    label: "Mobile",
    type: "tel",
    inputMode: "numeric",
    autoComplete: "tel",
    placeholder: "+61 412 345 678",
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    maxLength: 254,
    autoComplete: "email",
    required: true,
    placeholder: "accounts@company.com.au",
  },
  {
    name: "abn",
    label: "ABN (optional)",
    maxLength: 14,
    inputMode: "numeric",
    placeholder: "11 digit ABN",
  },
];

export default function InvoiceCustomerDialog({
  action,
  rctiEligibility = false,
  contact,
  businessUserID,
  darkMode,
  onClose,
  onCreated,
  onUpdated,
  onDeleted,
}) {
  const isDelete = action === "delete";
  const isCreate = action === "create";
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState(() =>
    isCreate
      ? {
          companyName: contact?.companyName || "",
          contactPerson: "",
          mobile: "+61",
          email: contact?.emailAddress || "",
          abn: "",
          gstRegistered: "",
          isLargeBusiness: "",
          isGovernmentEntity: "",
          paymentTerms: "14 Days",
          autoReminders: true,
        }
      : {}
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!isDelete && !isCreate);
  const [saving, setSaving] = useState(false);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const busy = loading || saving;
  const unsavedChanges = useUnsavedModalChanges({
    values: form,
    onClose,
    busy,
    enabled: !isDelete && !loading,
  });

  useEffect(() => {
    if (isDelete || isCreate) return undefined;
    const controller = new AbortController();
    const loadCustomer = async () => {
      try {
        const { data } = await axios.post(
          `${appConstants.baseURL}contact/fetchContactDetailsById`,
          {
            _id: contact._id,
            businessUserID,
          },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
            },
            signal: controller.signal,
            timeout: 30000,
          }
        );
        const record = data?.Result_Output?.[0];
        if (
          !data?.Result_Status ||
          !record ||
          String(record.businessUserID) !== String(businessUserID)
        ) {
          setError(data?.Result_Message || "Unable to load customer.");
          return;
        }
        setCustomer(record);
        const mobile = String(record.phoneNumber || "");
        setForm({
          companyName: record.companyName || "",
          contactPerson: record.name || "",
          mobile: mobile.startsWith("61") ? `+${mobile}` : mobile,
          email: record.emailAddress || "",
          abn: String(record.abnNumber || ""),
          paymentTerms: record.paymentTerms || "14 Days",
          autoReminders: record.invoiceReminder !== "No",
        });
      } catch (requestError) {
        if (!controller.signal.aborted)
          setError(
            requestError.response?.data?.Result_Message ||
              "Unable to load customer. Please try again."
          );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    loadCustomer();
    return () => controller.abort();
  }, [businessUserID, contact?._id, isCreate, isDelete]);

  const changeField = (name, value) => {
    if (name === "mobile") value = sanitizeAustralianMobileInput(value);
    setForm((previous) => ({ ...previous, [name]: value }));
    if (fieldErrors[name])
      setFieldErrors((previous) => ({
        ...previous,
        [name]: validateQuotationCustomerField(name, value),
      }));
  };

  const handleConfirm = async () => {
    if (busy || inFlightRef.current || (!isDelete && !isCreate && !customer))
      return;
    if (!isDelete) {
      const errors = validateQuotationCustomerForm(form);
      if (rctiEligibility) {
        if (!/^\d{11}$/.test(String(form.abn || "").replace(/\s/g, "")))
          errors.abn = "Enter an 11-digit ABN.";
        for (const key of [
          "gstRegistered",
          "isLargeBusiness",
          "isGovernmentEntity",
        ]) {
          if (!["true", "false"].includes(form[key]))
            errors[key] = "Select Yes or No.";
        }
      }
      setFieldErrors(errors);
      if (Object.values(errors).some(Boolean)) return;
    }
    inFlightRef.current = true;
    setSaving(true);
    setError("");
    try {
      const changes = isDelete
        ? null
        : {
            businessUserID,
            ...(rctiEligibility
              ? {
                  gstRegistered: form.gstRegistered === "true",
                  isLargeBusiness: form.isLargeBusiness === "true",
                  isGovernmentEntity: form.isGovernmentEntity === "true",
                }
              : {}),
            companyName: normalizeQuotationCustomerField(
              "companyName",
              form.companyName
            ),
            name: normalizeQuotationCustomerField(
              "contactPerson",
              form.contactPerson
            ),
            phoneNumber: form.mobile
              .replace(/[\s()-]/g, "")
              .replace(/^\+/, "")
              .replace(/^61$/, ""),
            emailAddress: normalizeQuotationCustomerField("email", form.email),
            abnNumber: form.abn.trim().replace(/[ -]/g, ""),
            paymentTerms: form.paymentTerms,
            invoiceReminder: form.autoReminders ? "Yes" : "No",
          };
      const { data } = await axios.post(
        `${appConstants.baseURL}contact/${
          isDelete ? "deleteContact" : isCreate ? "addContact" : "updateContact"
        }`,
        isDelete
          ? { _id: [contact._id], businessUserID }
          : isCreate
          ? {
              ...changes,
              source: rctiEligibility ? "rcti-supplier-quick-create" : "quotation-quick-create",
              contactType: rctiEligibility ? "Supplier" : "Customer",
              contactStatus: "Active",
            }
          : { ...changes, _id: customer._id },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 30000,
        }
      );
      if (!data?.Result_Status) {
        const errors = data?.Result_Output?.fieldErrors;
        if (errors && typeof errors === "object") setFieldErrors(errors);
        setError(
          errors?.form || data?.Result_Message ||
            `Unable to ${
              isDelete ? "delete" : isCreate ? "create" : "update"
            } customer.`
        );
        return;
      }
      if (isDelete) onDeleted(contact._id);
      else if (isCreate) await onCreated(data.Result_Output);
      else
        onUpdated({ ...customer, ...changes, ...(data.Result_Output || {}) });
      if (rctiEligibility && data.Result_Output?.contactVerifiedStatus === "Pending") {
        showResMsg(
          data.Result_Message ||
            "Contact saved. Awaiting email verification before it can be selected.",
          7000,
          Boolean(data.Result_Output?.verificationEmailSent)
        );
      } else if (isCreate)
        showDraftSavedMessage(
          rctiEligibility
            ? "Contact created successfully"
            : "Customer created successfully"
        );
      else
        showResMsg(
          `Customer ${isDelete ? "deleted" : "updated"} successfully.`,
          1500,
          true
        );
      onClose();
    } catch (requestError) {
      const responseErrors =
        requestError.response?.data?.Result_Output?.fieldErrors;
      if (responseErrors && typeof responseErrors === "object") {
        setFieldErrors((previous) => ({ ...previous, ...responseErrors }));
      }
      setError(
        responseErrors?.form || requestError.response?.data?.Result_Message ||
          `Unable to ${
            isDelete ? "delete" : isCreate ? "create" : "update"
          } customer. Please try again.`
      );
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const renderField = (fieldName) => {
    const field = fields.find((candidate) => candidate.name === fieldName);
    const inputId = isCreate
      ? `new-customer-${field.name}`
      : `invoice-customer-${field.name}`;
    return (
      <div className="quotation-field-wrap" key={field.name}>
        <label htmlFor={inputId}>
          {rctiEligibility && field.name === "abn" ? "ABN" : field.label}
          {(field.required || (rctiEligibility && field.name === "abn")) && (
            <span className="quotation-required-star"> *</span>
          )}
        </label>
        <div
          className={`quotation-input-box${
            fieldErrors[field.name] ? " error" : ""
          }`}
        >
          <input
            id={inputId}
            type={field.type || "text"}
            maxLength={field.maxLength}
            autoComplete={field.autoComplete}
            autoCapitalize={field.type === "email" ? "none" : undefined}
            spellCheck={field.type === "email" ? false : undefined}
            inputMode={field.inputMode}
            placeholder={field.placeholder}
            value={form[field.name] || ""}
            disabled={saving}
            autoFocus={isCreate && field.name === "companyName"}
            aria-invalid={Boolean(fieldErrors[field.name])}
            aria-describedby={
              fieldErrors[field.name] ? `${inputId}-error` : undefined
            }
            onChange={(event) => changeField(field.name, event.target.value)}
            onBlur={(event) => {
              const value = event.target.value;
              const errors = validateQuotationCustomerForm({
                ...form,
                [field.name]: value,
              });
              const firstField = Object.keys(errors)[0];
              const message = errors[field.name];
              setFieldErrors((previous) =>
                message || previous[firstField] ? errors : {}
              );
              if (!message) {
                setForm((previous) => ({
                  ...previous,
                  [field.name]: normalizeQuotationCustomerField(
                    field.name,
                    value
                  ),
                }));
              }
            }}
          />
        </div>
        {fieldErrors[field.name] && (
          <span
            id={`${inputId}-error`}
            className="quotation-customer-field-error"
            role="alert"
          >
            {fieldErrors[field.name]}
          </span>
        )}
      </div>
    );
  };

  return (
    <CatalogueItemModal
      darkMode={darkMode}
      isSaving={busy}
      onClose={unsavedChanges.requestClose}
    >
      <div
        className="quotation-modal-dialog quotation-customer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-customer-dialog-title"
      >
        <div className="quotation-modal-header">
          <h3 id="invoice-customer-dialog-title">
            {isDelete
              ? "Delete customer?"
              : isCreate
              ? rctiEligibility
                ? "Add new contact"
                : "New customer"
              : "Edit customer"}
          </h3>
          <button
            type="button"
            className="quotation-dialog-close-btn"
            aria-label="Close"
            disabled={busy}
            onClick={unsavedChanges.requestClose}
          >
            <LuX />
          </button>
        </div>
        <div className="quotation-modal-body" aria-busy={loading}>
          {error && (
            <div className="quotation-modal-alert" role="alert">
              <LuCircleAlert />
              <span>{error}</span>
            </div>
          )}
          {isDelete ? (
            <p>
              Delete {contact.companyName || contact.name}? This cannot be
              undone. Customers used in quotations, invoices or RCTI cannot be
              deleted.
            </p>
          ) : loading ? (
            <p role="status">
              <CircularProgress size={16} /> Loading customer…
            </p>
          ) : (
            (customer || isCreate) && (
              <>
                <p className="quotation-modal-caption">
                  {isCreate
                    ? `Quick create · Contact type: ${
                        rctiEligibility ? "Supplier" : "Customer"
                      } · Verification required`
                    : "Update customer details"}
                </p>
                {renderField("companyName")}
                <div className="quotation-modal-form-row">
                  {renderField("contactPerson")}
                  {renderField("mobile")}
                </div>
                {renderField("email")}
                <div className="quotation-modal-form-row">
                  {renderField("abn")}
                  <div className="quotation-field-wrap">
                    <label>Payment terms</label>
                    <InvoiceSelect
                      dark={darkMode}
                      aria-label="Payment terms"
                      disabled={saving}
                      value={form.paymentTerms}
                      onChange={(event) =>
                        changeField("paymentTerms", event.target.value)
                      }
                    >
                      {paymentTermsOptions.map((term) => (
                        <option key={term} value={term}>
                          {term}
                        </option>
                      ))}
                    </InvoiceSelect>
                    {fieldErrors.paymentTerms && (
                      <span
                        className="quotation-customer-field-error"
                        role="alert"
                      >
                        {fieldErrors.paymentTerms}
                      </span>
                    )}
                  </div>
                </div>
                {rctiEligibility && (
                  <div className="quotation-modal-form-row">
                    {[
                      ["gstRegistered", "GST registered"],
                      ["isLargeBusiness", "Large business"],
                      ["isGovernmentEntity", "Government entity"],
                    ].map(([key, label]) => (
                      <div className="quotation-field-wrap" key={key}>
                        <label>
                          {label}{" "}
                          <span className="quotation-required-star">*</span>
                        </label>
                        <InvoiceSelect
                          dark={darkMode}
                          aria-label={label}
                          disabled={saving}
                          value={form[key]}
                          onChange={(event) =>
                            changeField(key, event.target.value)
                          }
                        >
                          <option value="">Select Yes or No</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </InvoiceSelect>
                        {fieldErrors[key] && (
                          <span
                            className="quotation-customer-field-error"
                            role="alert"
                          >
                            {fieldErrors[key]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className="quotation-modal-switch-row"
                  role="switch"
                  tabIndex={0}
                  aria-checked={form.autoReminders}
                  onClick={() =>
                    !saving && changeField("autoReminders", !form.autoReminders)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      if (!saving)
                        changeField("autoReminders", !form.autoReminders);
                    }
                  }}
                >
                  <div
                    className={`quotation-modal-switch-track${
                      form.autoReminders ? " active" : ""
                    }`}
                  >
                    <div className="quotation-modal-switch-thumb" />
                  </div>
                  <span className="quotation-modal-switch-label">
                    Send invoice reminders automatically
                  </span>
                </div>
                <p className="quotation-modal-caption">
                  Address, bank details and logo can be added later from
                  Contacts.
                </p>
              </>
            )
          )}
        </div>
        <div className="quotation-modal-footer">
          <button
            type="button"
            className="quotation-modal-cancel-btn"
            disabled={busy}
            onClick={unsavedChanges.requestClose}
          >
            Cancel
          </button>
          {isCreate ? (
            <button
              type="button"
              className="quotation-modal-submit-btn"
              disabled={saving}
              aria-busy={saving}
              onClick={handleConfirm}
            >
              {saving ? "Saving..." : rctiEligibility ? "Save & send verification" : "Save customer"}
            </button>
          ) : (
            <button
              type="button"
              className={`quotation-modal-submit-btn${
                isDelete ? " quotation-customer-delete-confirm" : ""
              }`}
              disabled={busy || (!isDelete && !customer)}
              aria-busy={saving}
              onClick={handleConfirm}
            >
              {saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : isDelete ? (
                <LuTrash2 />
              ) : (
                <LuSave />
              )}
              {saving
                ? isDelete
                  ? "Deleting…"
                  : "Saving…"
                : isDelete
                ? "Delete customer"
                : "Save changes"}
            </button>
          )}
        </div>
      </div>
      {unsavedChanges.dialog}
    </CatalogueItemModal>
  );
}
